import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Chip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import apiService from '../../services/api';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 600 },
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  overflow: 'auto'
};

const SimulationExportModal = ({ open, onClose, simulationId, token }) => {
  // State for export configuration
  const [config, setConfig] = useState({
    format: 'pdf',
    includeSystemPrompts: false,
    includeTranscript: true,
    includeSummary: true,
    includeReview: true,
    includeMetadata: true
  });

  // State for export availability
  const [exportConfig, setExportConfig] = useState(null);
  const [checkingConfig, setCheckingConfig] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  // Check export configuration when modal opens
  useEffect(() => {
    if (open && simulationId && token) {
      checkExportConfig();
    }
  }, [open, simulationId, token]);

  const checkExportConfig = async () => {
    setCheckingConfig(true);
    setError(null);
    try {
      const response = await apiService.getSimulationExportConfig(simulationId, token);
      setExportConfig(response);
    } catch (error) {
      console.error('Error checking export config:', error);
      setError('Failed to check export configuration');
    } finally {
      setCheckingConfig(false);
    }
  };

  const handleConfigChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleExport = async () => {
    setDownloading(true);
    setError(null);
    
    try {
      const response = await apiService.exportSimulation(simulationId, config, token);
      
      // Handle different formats
      if (config.format === 'pdf') {
        // Create blob from response data
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `simulation-export-${simulationId}-${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else if (config.format === 'markdown') {
        // Handle markdown download
        const blob = new Blob([response.data], { type: 'text/markdown' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `simulation-export-${simulationId}-${Date.now()}.md`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // Handle JSON
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `simulation-export-${simulationId}-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
      
      // Close modal after successful download
      onClose();
    } catch (error) {
      console.error('Error exporting simulation:', error);
      setError('Failed to export simulation. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (checkingConfig) {
    return (
      <Modal open={open} onClose={onClose}>
        <Box sx={modalStyle}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h5" component="h2" gutterBottom>
          Export Simulation
        </Typography>
        
        {exportConfig && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                icon={<CheckCircleIcon />}
                label={`${exportConfig.metadata?.messageCount || 0} messages`}
                size="small"
                color="primary"
              />
              <Chip
                label={`Client: ${exportConfig.metadata?.clientPersona}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`Therapist: ${exportConfig.metadata?.therapistPersona}`}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          {/* Format Selection */}
          <FormControl fullWidth>
            <InputLabel>Export Format</InputLabel>
            <Select
              value={config.format}
              label="Export Format"
              onChange={handleConfigChange('format')}
            >
              <MenuItem value="pdf">PDF Document</MenuItem>
              <MenuItem value="markdown">Markdown</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
            </Select>
          </FormControl>

          <Divider />

          {/* Content Options */}
          <Typography variant="subtitle1" fontWeight="bold">
            Content to Include
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeTranscript}
                onChange={handleConfigChange('includeTranscript')}
                disabled
              />
            }
            label="Transcript (always included)"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeSystemPrompts}
                onChange={handleConfigChange('includeSystemPrompts')}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                System Prompts
                <Typography variant="caption" color="text.secondary">
                  (for analysis purposes)
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeSummary}
                onChange={handleConfigChange('includeSummary')}
                disabled={!exportConfig?.hasSummary}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                Summary
                {!exportConfig?.hasSummary && (
                  <Chip
                    icon={<WarningIcon />}
                    label="Not yet generated"
                    size="small"
                    color="warning"
                  />
                )}
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeReview}
                onChange={handleConfigChange('includeReview')}
                disabled={!exportConfig?.hasReview}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                Review
                {!exportConfig?.hasReview && (
                  <Chip
                    icon={<WarningIcon />}
                    label="Not yet generated"
                    size="small"
                    color="warning"
                  />
                )}
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeMetadata}
                onChange={handleConfigChange('includeMetadata')}
              />
            }
            label="Metadata (timestamps, configuration)"
          />

          <Divider />

          {/* Info Alert */}
          {config.includeSystemPrompts && (
            <Alert severity="info" icon={<InfoIcon />}>
              System prompts will be included in the export. These contain internal configuration details.
            </Alert>
          )}

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={onClose} disabled={downloading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleExport}
              disabled={downloading || checkingConfig}
              startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            >
              {downloading ? 'Exporting...' : 'Export'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

export default SimulationExportModal;